/**
 * useModelPreview — requests a canvas snapshot for one Discover card, lazily.
 *
 * Only fires when `enabled` (i.e. the card has no $icon) AND the card has
 * scrolled into view. Returns a ref to attach to the card's cover element,
 * the resulting data-URL (or null), and a coarse status for fade-in.
 */
import { useEffect, useRef, useState } from 'react';
import { requestPreview } from './modelPreviewService';

export type PreviewStatus = 'idle' | 'pending' | 'done' | 'failed';

export function useModelPreview(
  id: string | undefined,
  version: string | number | undefined,
  enabled: boolean,
) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<PreviewStatus>('idle');

  // A new model, version or enabled flag drops the previous snapshot during
  // render, so a card never shows another model's preview for a frame.
  const [previewFor, setPreviewFor] = useState({ id, version, enabled });
  if (previewFor.id !== id || previewFor.version !== version || previewFor.enabled !== enabled) {
    setPreviewFor({ id, version, enabled });
    setDataUrl(null);
    setStatus('idle');
  }

  useEffect(() => {
    if (!enabled || !id) return;
    const node = ref.current;
    if (!node) return;

    let alive = true;
    let started = false;

    const start = () => {
      if (started) return;
      started = true;
      setStatus('pending');
      // fire-and-forget: a preview that cannot be rendered at all (e.g. the canvas
      // chunk failed to load) settles as no snapshot, so the card keeps its fallback cover
      void requestPreview(id, version)
        .catch(() => null)
        .then((url) => {
          if (!alive) return;
          setDataUrl(url);
          setStatus(url ? 'done' : 'failed');
        });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          start();
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(node);

    return () => {
      alive = false;
      observer.disconnect();
    };
  }, [id, version, enabled]);

  return { ref, dataUrl, status };
}
